/**
 * Trusted Circle (§22.5) — members + invitations API client.
 *
 * Permission atoms and roles are kept in sync with
 * services/users/app/permissions.py. If you add an atom there, mirror it here
 * AND in CIRCLE_ROLE_DEFAULTS so the invite UI pre-fills correctly.
 */
import { api } from './client';

export type CircleRole =
  | 'owner'
  | 'parent'
  | 'grandparent'
  | 'babysitter'
  | 'school_driver'
  | 'aunt_uncle'
  | 'custom';

export type CirclePermission =
  | 'manage_family'
  | 'view_location'
  | 'view_location_scheduled'
  | 'receive_sos'
  | 'call_child'
  | 'unlock_device'
  | 'arrival_notify'
  | 'view_timeline'
  | 'view_screen_time';

export const ALL_PERMISSIONS: CirclePermission[] = [
  'manage_family',
  'view_location',
  'view_location_scheduled',
  'receive_sos',
  'call_child',
  'unlock_device',
  'arrival_notify',
  'view_timeline',
  'view_screen_time',
];

export const PERMISSION_COPY: Record<CirclePermission, { label: string; hint: string }> = {
  manage_family: { label: 'Manage family', hint: 'Change rules, invite others.' },
  view_location: { label: 'See live location', hint: 'Always-on map access.' },
  view_location_scheduled: {
    label: 'See location during schedule',
    hint: 'Map access only inside the time window below.',
  },
  receive_sos: { label: 'Receive SOS alerts', hint: 'Pushed when the child triggers panic.' },
  call_child: { label: 'Call the child', hint: 'Start a voice call to the child device.' },
  unlock_device: { label: 'Unlock device', hint: 'Issue lock/unlock/pause commands.' },
  arrival_notify: { label: 'Arrival notifications', hint: 'School-exit + near-home pings.' },
  view_timeline: { label: 'See the timeline', hint: 'Read the day-by-day activity feed.' },
  view_screen_time: { label: 'See screen time', hint: 'Read app usage and weekly digests.' },
};

export const ROLE_COPY: Record<CircleRole, { label: string; hint: string }> = {
  owner: { label: 'Primary parent', hint: 'Family root — full control, cannot be removed.' },
  parent: { label: 'Parent', hint: 'Full parental control.' },
  grandparent: { label: 'Grandparent', hint: 'Location + SOS + call.' },
  babysitter: { label: 'Babysitter', hint: 'Location + arrival pings, on schedule.' },
  school_driver: { label: 'School driver', hint: 'Arrival notifications only.' },
  aunt_uncle: { label: 'Aunt / Uncle', hint: 'Location during schedule + call.' },
  custom: { label: 'Custom', hint: 'Pick permissions individually.' },
};

export const ROLE_DEFAULTS: Record<CircleRole, CirclePermission[]> = {
  owner: [...ALL_PERMISSIONS],
  parent: [...ALL_PERMISSIONS],
  grandparent: ['view_location', 'receive_sos', 'call_child', 'view_timeline'],
  babysitter: ['view_location_scheduled', 'arrival_notify'],
  school_driver: ['arrival_notify'],
  aunt_uncle: ['view_location_scheduled', 'call_child'],
  custom: [],
};

export interface CircleSchedule {
  days: number; // bit 0 = Mon, bit 6 = Sun (Mon-Fri = 0x1F, every day = 0x7F)
  from_min: number;
  to_min: number;
}

export interface CircleMember {
  id: string;
  family_id: string;
  user_id: string;
  role: CircleRole;
  display_name: string | null;
  avatar_url: string | null;
  permissions: CirclePermission[];
  schedule: CircleSchedule | null;
  child_scope: string[] | null;
  invited_by_user_id: string | null;
  joined_at: string;
  is_caller: boolean;
}

export interface CircleMemberUpdate {
  role?: CircleRole;
  display_name?: string | null;
  permissions?: CirclePermission[];
  schedule?: CircleSchedule | null;
  child_scope?: string[] | null;
}

export interface CircleInvitation {
  id: string;
  family_id: string;
  email: string;
  display_name: string | null;
  role: CircleRole;
  permissions: CirclePermission[];
  schedule: CircleSchedule | null;
  child_scope: string[] | null;
  status: 'pending' | 'accepted' | 'declined' | 'revoked' | 'expired';
  expires_at: string;
  invited_by_user_id: string;
  accepted_by_user_id: string | null;
  accepted_at: string | null;
  created_at: string;
  token: string;
}

export interface CircleInvitationCreate {
  email: string;
  display_name?: string;
  role: CircleRole;
  permissions?: CirclePermission[];
  schedule?: CircleSchedule;
  child_scope?: string[];
}

export interface CircleInvitationPreview {
  family_id: string;
  family_name: string;
  role: CircleRole;
  permissions: CirclePermission[];
  display_name: string | null;
  expires_at: string;
  status: string;
}

export const circleApi = {
  listMembers: () => api<CircleMember[]>('/v1/families/me/members'),
  updateMember: (id: string, payload: CircleMemberUpdate) =>
    api<CircleMember>(`/v1/families/me/members/${id}`, { method: 'PATCH', body: payload }),
  removeMember: (id: string) =>
    api<void>(`/v1/families/me/members/${id}`, { method: 'DELETE' }),

  listInvitations: () => api<CircleInvitation[]>('/v1/families/me/invitations'),
  createInvitation: (payload: CircleInvitationCreate) =>
    api<CircleInvitation>('/v1/families/me/invitations', { method: 'POST', body: payload }),
  revokeInvitation: (id: string) =>
    api<void>(`/v1/families/me/invitations/${id}`, { method: 'DELETE' }),

  previewInvitation: (token: string) =>
    api<CircleInvitationPreview>(`/v1/invitations/${token}`),
  acceptInvitation: (token: string) =>
    api<CircleMember>(`/v1/invitations/${token}/accept`, { method: 'POST' }),
  declineInvitation: (token: string) =>
    api<void>(`/v1/invitations/${token}/decline`, { method: 'POST' }),
};
